import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Hero — full-screen, video background, dark.
 *
 * The video plays muted + autoplay + playsInline so iOS doesn't block it.
 * A vertical gradient overlay sits on top so text stays legible no matter
 * what frame of the video is showing.
 */
export function HomeHero() {
  const scrollToFeatures = () => {
    const el = document.getElementById('home-features');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {/* Background video — bundled with the app (no third-party CDN
          dependency). Compressed from a 14.8 MB source to 684 KB MP4 +
          1 MB WebM via ffmpeg; the poster JPG (~50 KB) renders instantly
          while the video buffers, so users never see a black hero.
          - autoPlay + muted + playsInline are required for browser auto-start
          - WebM listed first so Chrome/Firefox pick the smaller VP9 file;
            Safari falls through to the MP4 H.264.
          - preload="auto" so the browser starts buffering right away. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        poster="/hero-poster.jpg"
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="/hero.webm" type="video/webm" />
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      {/* Lighter overlay so the video stays visible behind the text. */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/55 via-black/30 to-black/60" />

      {/* Content */}
      <div className="relative z-20 h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/15 backdrop-blur-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
          <span className="text-xs uppercase tracking-[0.2em] text-white/80 font-medium">
            ORBIT · by Pear Media
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
          className="mt-8 text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white max-w-5xl leading-[1.05]"
        >
          Your creative pipeline,
          <br />
          <span className="bg-gradient-to-r from-orange-300 via-orange-400 to-orange-500 bg-clip-text text-transparent">
            in one place.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
          className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
        >
          From buyer brief to QC-passed asset to launch-ready creative — the workflow library your team will actually use.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35, ease: 'easeOut' }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-3"
        >
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg shadow-orange-500/30 transition-all hover:scale-[1.02]"
          >
            Sign in to Orbit
            <ArrowRight size={18} />
          </Link>
          <button
            onClick={scrollToFeatures}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white font-semibold backdrop-blur-sm transition-colors"
          >
            <Play size={16} fill="currentColor" />
            See it in action
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/60"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
